import React, { useEffect, useState, useContext } from "react";
import Caption from "./Typography/Caption";
import routes from "../setup/routes";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { KEYS } from "../setup/entitlements";
import Entitlement, { isAllowed } from "./Entitlements";
import { DispatchContext, StateContext } from "../store/context";
import Logo from "../static/light-logo.svg";
import DarkLogo from "../static/darkLogo.svg";
import IconLogoLight from "../static/logo icon light.jpeg";
import IconLogoDark from "../static/logo icon dark.jpeg";
import { UPDATE_BASE_CURRENCY, UPDATE_USER } from "../store/types";
import { Button, Select, Tooltip } from "antd";
import DarkModeSwitcher from "./darkModeSwithcer";
import PaletteSwitcher from "./paletteSwitcher";
import { usePalette } from "../hooks/usePalette";
import ROUTES from "../setup/routes";
import { saveToSessionStorage } from "../utils/sessionStorageState";
import { SIDEBAR_ICON_MAP } from "./sidebarIcons";
import {
  ChevronLeft,
  ChevronRight,
  LogOut,
  FileText,
  Sparkles,
  LogIn,
} from "lucide-react";

import { terminateUserSession } from "../services/session.service";
import { socket } from "../config/socketService";
import initialState from "../store/state";
import "../styles/glass-tokens.css";
import "./sidebar.theme.css";
import { disconnectChatSocket } from "../config/socketService";

type SidebarType = {
  isCollapsed: boolean;
  setisCollapsed: any;
  toggleCollapse: (value: any) => void;
};

type SidebarItem = {
  label: string;
  icon: string;
  path: string;
  entitlements: string[];
  adminOnly?: boolean;
  allowAdminWithoutEntitlement?: boolean;
  requiresAuth?: boolean;
};

const normalizePath = (value: string = "") => {
  const withLeadingSlash = value.startsWith("/") ? value : `/${value}`;
  const normalized = withLeadingSlash.replace(/\/+/g, "/").replace(/\/$/, "");
  return normalized || "/";
};

const matchesMenuPath = (menuPath: string, currentPath: string) => {
  const normalizedMenuPath = normalizePath(menuPath);
  const normalizedCurrentPath = normalizePath(currentPath);
  if (normalizedMenuPath === "/") {
    return normalizedCurrentPath === "/";
  }
  return (
    normalizedCurrentPath === normalizedMenuPath ||
    normalizedCurrentPath.startsWith(`${normalizedMenuPath}/`)
  );
};

const Sidebar = (props: SidebarType) => {
  const { isCollapsed, setisCollapsed, toggleCollapse } = props;
  const location = useLocation();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<string>("Dashboard");
  const { authUser, baseCurrency } = useContext(StateContext);

  const allowed = authUser?.allowed || [];
  const username = authUser?.username || "";
  const isAdminUserOne =
    [1, 2, 3].includes(Number(authUser?.id || authUser?.userId || 0)) ||
    authUser?.role_name === "admin";
  const isAuthenticated =
    !!authUser?.token || !!String(localStorage.getItem("token") || "").trim();

  const items: SidebarItem[] = [
    {
      label: "Beginner Analysis",
      icon: "BeginnerAnalysis",
      path: `${routes.DASHBOARD}/${routes.BEGINNER}`,
      entitlements: [KEYS.BEGINNER_ANALYSIS_VIEW],
    },
    {
      label: "Detailed Analysis",
      icon: "SingleStockAnalysis",
      path: `${routes.DASHBOARD}/${routes.SINGLE_STOCK_ANALYSIS}`,
      entitlements: [KEYS.SINGLE_STOCK_ANALYSIS_VIEW],
    },
    {
      label: "Compare",
      icon: "fundemantal",
      path: `${routes.DASHBOARD}/${routes.OVERVIEW}`,
      entitlements: [KEYS.COMPARE],
    },
    {
      label: "Dynamic Graphs",
      icon: "bar",
      path: `${routes.DASHBOARD}/${routes.DYNAMIC_GRAPHS}`,
      entitlements: [KEYS.VIEW_DYNAMIC_GRAPH],
    },
    {
      label: "Financials",
      icon: "Dollar",
      path: `${routes.DASHBOARD}/${routes.FINANCIALS}`,
      entitlements: [KEYS.FINANCIAL_VIEW],
    },
    {
      label: "Screener",
      icon: "clipboard",
      path: `${routes.DASHBOARD}/${routes.SCREENER}`,
      entitlements: [KEYS.STOCK_SCREENER_VIEW],
    },
    {
      label: "Market Heatmap",
      icon: "Heatmap",
      path: `${routes.DASHBOARD}/${routes.MARKET_HEATMAP}`,
      entitlements: [KEYS.MARKET_HEATMAP_VIEW],
    },
    {
      label: "Watchlist",
      icon: "Eye",
      path: `${routes.DASHBOARD}/${routes.WATCHLIST}`,
      entitlements: [KEYS.WATCHLIST_VIEW],
    },
    {
      label: "AI Recommendations ",
      icon: "Screen",
      path: `${routes.DASHBOARD}/${routes.AI_ANALYZE}`,
      entitlements: [KEYS.AI_RECOMMENDATION_VIEW],
      requiresAuth: true,
    },
    {
      label: "Stock Assistant",
      icon: "StockAssistant",
      path: `${routes.DASHBOARD}/${routes.STOCK_ASSISTANT}`,
      entitlements: [KEYS.STOCK_ASSISTANT_VIEW],
      requiresAuth: true,
    },
    {
      label: "Support",
      icon: "Support",
      path: `${routes.DASHBOARD}/${routes.SUPPORT}`,
      entitlements: [KEYS.SUPPORT_VIEW],
    },
    {
      label: "Stock Chatbot",
      icon: "ChatBubble",
      path: `${routes.DASHBOARD}/${routes.STOCK_CHATBOT}`,
      entitlements: [KEYS.STOCK_CHATBOT_VIEW],
    },
    {
      label: "Technical Analysis",
      icon: "TechnicalAnalysis",
      path: `${routes.DASHBOARD}/${routes.TECHNICAL_ANALYSIS}`,
      entitlements: [KEYS.TECHNICAL_ANALYSIS_VIEW],
    },
    {
      label: "Falcon Data Entry",
      icon: "DocumentText",
      path: `${routes.DASHBOARD}/${routes.ANNUAL_REPORT_DATA_ENTRY}`,
      entitlements: [KEYS.FALCON_DATA_ENTRY_VIEW],
      allowAdminWithoutEntitlement: true,
    },
    {
      label: "Falcon Extraction",
      icon: "DocumentText",
      path: `${routes.DASHBOARD}/${routes.ANNUAL_REPORT_EXTRACTION}`,
      entitlements: [KEYS.FALCON_EXTRACTION_VIEW],
      allowAdminWithoutEntitlement: true,
    },
    {
      label: "Falcon Senior QC",
      icon: "DocumentText",
      path: `${routes.DASHBOARD}/${routes.ANNUAL_REPORT_QC}`,
      entitlements: [KEYS.FALCON_SENIOR_QC_VIEW],
      requiresAuth: true,
    },
    {
      label: "Falcon Log Monitor",
      icon: "Pulse",
      path: `${routes.DASHBOARD}/${routes.FALCON_LOG_MONITOR}`,
      entitlements: [KEYS.FALCON_LOG_MONITOR_VIEW],
      allowAdminWithoutEntitlement: true,
    },
    {
      label: "Annual Report Entry",
      icon: "DocumentText",
      path: `${routes.DASHBOARD}/${routes.ANNUAL_REPORT_MANUAL_ENTRY}`,
      entitlements: [KEYS.ANNUAL_REPORT_ENTRY_VIEW],
    },
    {
      label: "Graph Editor",
      icon: "Pulse",
      path: `${routes.DASHBOARD}/${routes.GRAPH_EDITOR}`,
      entitlements: [KEYS.GRAPH_EDITOR],
    },
    // {
    //   label: "Technical Analysis",
    //   icon: "Dashboard",
    //   path: `${routes.DASHBOARD}/${routes.TECHNICAL_ANALYSIS}`,
    //   entitlements: [KEYS.COMPARE],
    // },
    {
      label: "User Management",
      icon: "User",
      path: `${routes.USER_MANAGEMENT}/${routes.USERS}`,
      entitlements: [KEYS.USERS_VIEW, KEYS.USER_MANAGEMENT_VIEW],
    },
    {
      label: "Ad Management",
      icon: "DocumentText",
      path: routes.ADS,
      entitlements: [KEYS.AD_MANAGEMENT],
    },
    {
      label: "Equation Builder",
      icon: "NineDots",
      path: routes.EQUATION_BUILDER,
      entitlements: [KEYS.EQUATIONS_VIEW, KEYS.EQUATIONS_ADD],
    },
    {
      label: "Metrics Management",
      icon: "DocumentText",
      path: routes.METRICS_MANAGEMENT,
      entitlements: [KEYS.METRICS_VIEW, KEYS.METRICS_ADD],
    },
    {
      label: "Stock Market",
      icon: "DocumentText",
      path: routes.STOCK_MARKET,
      entitlements: [KEYS.STOCK_MARKET_VIEW],
    },
    {
      label: "Sector",
      icon: "DocumentText",
      path: routes.SECTOR,
      entitlements: [KEYS.SECTOR_VIEW, KEYS.SECTOR_ADD],
    },
    {
      label: "Company Quarter",
      icon: "DocumentText",
      path: routes.COMPANY_QUARTER,
      entitlements: [KEYS.COMPANY_QUARTERS_VIEW, KEYS.COMPANY_QUARTERS_ADD],
    },
    // // {label: 'PE Valuation', icon: 'signal', path: routes.PE_VALUATIONS},
    // // {label: 'Revenues', icon: 'dots-hr', path: routes.REVENUES},
    // // {label: 'Gross Profit', icon: 'list', path: routes.GROSS_PROFITS},
    // // {label: 'Net Profit', icon: 'list', path: routes.NET_PROFITS},
    {
      label: "Stock Listings",
      icon: "DocumentText",
      path: routes.STOCK_LISTINGS,
      entitlements: [KEYS.GET_ALL_STOCKS],
    },

    // { label: "Components", icon: "Ribbon", path: "/components" },
  ];
  const bottomItems = [
    {
      label: "My Profile",
      icon: "User",
      path: `${routes.DASHBOARD}/${routes.CUSTOMER_PROFILE}`,
      entitlements: [KEYS.MY_PROFILE],
    },
    {
      label: "Settings",
      icon: "DocumentText",
      path: routes.SETTINGS,
      entitlements: [KEYS.SETTINGS],
    },
  ];
  const handleMenuSelection = (e: any) => {
    setSelected(e.currentTarget.id);
  };

  const hasSidebarAccess = (item: any) => {
    if (item?.requiresAuth && !isAuthenticated) {
      return false;
    }
    if (item?.adminOnly && !isAdminUserOne) {
      return false;
    }
    if (isAllowed(item?.entitlements || [], allowed)) {
      return true;
    }
    if (isAdminUserOne && item?.allowAdminWithoutEntitlement) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    const filteredItems: any = items.filter((item: any) =>
      hasSidebarAccess(item),
    );
    const normalizedPath = normalizePath(location.pathname);
    const activeItem = filteredItems.find((item: any) =>
      matchesMenuPath(item.path, location.pathname),
    );
    if (activeItem) {
      setSelected(activeItem.label);
      return;
    }

    const stockDetailBasePath = `/${String(routes.STOCKS || "stocks/:id").split("/:")[0]}`;
    const isStockDetailPath =
      normalizedPath === stockDetailBasePath ||
      normalizedPath.startsWith(`${stockDetailBasePath}/`);
    if (isStockDetailPath) {
      const stockListingItem = filteredItems.find(
        (item: any) =>
          normalizePath(item.path) === normalizePath(routes.STOCK_LISTINGS),
      );
      if (stockListingItem) {
        setSelected(stockListingItem.label);
      }
      return;
    }

    const isHomePath = normalizedPath === "/";
    if (isHomePath && filteredItems.length) {
      navigate(filteredItems[0].path);
      setSelected(filteredItems[0].label);
    }
  }, [allowed, isAdminUserOne, location.pathname, navigate]);

  const goHome = () => {
    // Was window.location.href = HOMEPAGE (a full-page redirect to the
    // public valueit.io marketing site) — the logo now takes a logged-
    // in user back to their own dashboard instead, via the same client-
    // side route the sidebar's own "landed on /" effect above already
    // resolves to the first allowed item.
    navigate(routes.HOME);
  };
  const dispatch = useContext(DispatchContext);

  const handleChange = (e: any) => {
    dispatch({ type: UPDATE_BASE_CURRENCY, payload: e });
    setDefaultCurrency(e);
  };

  const currencyOptions = [
    { label: "INR", value: "INR" },
    { label: "LKR", value: "LKR" },
    { label: "USD", value: "USD" },
    { label: "QAR", value: "QAR" },
    { label: "AED", value: "AED" },
    { label: "SAR", value: "SAR" },
    { label: "KWD", value: "KWD" },
    { label: "SGD", value: "SGD" },
    { label: "SDG", value: "SDG" },
  ];

  const [darkMode, setDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem("darkMode");
    return savedTheme === "true" || false;
  });

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    localStorage.setItem("darkMode", darkMode.toString());
  }, [darkMode]);

  const handleLogout = async () => {
    try {
      const isTerminated: boolean = await terminateUserSession(username);
      if (isTerminated) {
        // if (socket && socket.connected) {
        //   socket.disconnect();
        // }
        localStorage.removeItem("token");
        localStorage.removeItem("is_first_login");
        localStorage.removeItem("is_manually_created");
        localStorage.removeItem("beginner_selected_ticker");
        localStorage.removeItem("financials_selected_ticker");
        localStorage.removeItem("single_stock_analysis_selected_ticker");
        localStorage.removeItem("fundamental_analysis_selection");
        localStorage.removeItem("dynamic_graphs_selected_tickers");
        localStorage.removeItem("dynamic_graphs_selected_parameters");
        disconnectChatSocket();
        saveToSessionStorage({});
        dispatch({ type: UPDATE_USER, payload: initialState.authUser });
        navigate(ROUTES.LOGIN);
      }
    } catch (e: any) {
      console.log("Error", e.message);
    }
  };

  const toggleDarkMode = () => setDarkMode((prevMode) => !prevMode);
  const { palette, togglePalette } = usePalette();

  const [defaultCurrency, setDefaultCurrency] = useState(baseCurrency);

  const handleLogIn = () => {
    navigate(ROUTES.LOGIN);
  };

  useEffect(() => {
    setDefaultCurrency(baseCurrency);
  }, [baseCurrency]);

  return (
    <div
      // Always w-full: this box should fill whatever width Dashboard.tsx's
      // grid gives it (col-span-1 collapsed vs. col-span-2/3 expanded) —
      // hardcoding w-20 here for the collapsed case fixed this element's
      // own width to exactly 80px regardless of how wide col-span-1
      // actually computes to (wider than 80px on most viewports), leaving
      // a visible gap between this box's right edge and the content pane
      // next to it. The nav column and its items already center
      // themselves within whatever width this root ends up being.
      className="flex !overflow-x-hidden flex-col h-full !max-h-[95%] relative transition-all duration-300 bg-[color:var(--vi-bg-page)] border border-[color:var(--vi-card-border)] rounded-lg w-full"
      style={{
        // A full border (was border-r only) so the new rounded corners
        // read as an actual card edge on every side, not just the
        // right. The glow layers a soft accent-tinted bloom on top of
        // the app's standard card elevation shadow (--vi-shadow-card) —
        // same tokens every themed page already uses, rather than a
        // one-off hardcoded color. Kept a modest blur radius since this
        // box sits inside Dashboard.tsx's `!overflow-hidden` column
        // wrapper — a wider glow would just get clipped at that
        // wrapper's edges without ever becoming visible.
        boxShadow:
          "var(--vi-shadow-card), 0 0 20px color-mix(in srgb, var(--vi-accent) 25%, transparent)",
      }}
    >
      {/* Logo — its own shrink-0 row, outside the scrollable nav area
          below, so it stays put while the nav list scrolls. Also the new
          anchor for the collapse/expand toggle button (see comment on
          that button below) — `relative` so it positions against this
          short row instead of the whole sidebar. */}
      <div
        className={`w-full flex flex-col shrink-0 relative ${
          isCollapsed ? "items-center" : "items-start"
        }`}
      >
        <div className="w-full flex items-start gap-x-2 px-4 pt-5 pb-2">
          <div className="transition-all flex items-center justify-center duration-300 w-full">
            <img
              src={
                isCollapsed
                  ? darkMode
                    ? IconLogoDark
                    : IconLogoLight
                  : darkMode
                    ? DarkLogo
                    : Logo
              }
              // The source SVG's own viewBox is 66x26 (a wide wordmark) —
              // sized by width with h-auto so it scales proportionally
              // instead of being stretched to fill a tall, full-width
              // box (which is what made it look cropped/distorted with
              // no breathing room around it). Collapsed state swaps in the
              // square icon-only logo instead of shrinking the wordmark.
              className={`transition-all duration-300 h-auto cursor-pointer ${
                isCollapsed ? "w-10 rounded-md" : "w-[150px]"
              }`}
              onClick={(event) => {
                event.preventDefault();
                goHome();
              }}
              onContextMenu={(event) => event.preventDefault()}
            />
          </div>
        </div>

        {/* Collapse/expand handle — previously anchored to the whole
            sidebar box at a fixed top:60px, which happened to land
            almost exactly on the first nav row once collapsed (the nav
            column shrinks to the same w-20 the sidebar itself is, so
            the handle's `right:-4px` sat right on top of that row's
            centered, highlighted icon instead of in the gutter beside
            it — the "doesn't look good when collapsed" glitch). Anchored
            to this short logo row instead, with its own top offset per
            state, so it always sits beside the logo — never over the
            nav list — in both states. Redesigned from a bare floating
            icon into a small solid circular tab (styling in
            sidebar.theme.css's .vi-sidebar-collapse-btn) that pokes half
            onto the sidebar's edge — a real resting affordance instead
            of an icon with no visible button around it. */}
        <button
          onClick={toggleCollapse}
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="vi-sidebar-collapse-btn absolute flex items-center justify-center rounded-full"
          style={{
            // Was -16px (deliberately poking half outside the sidebar's
            // edge) — but the sidebar's own root box has `!overflow-x-
            // hidden` (see its className above), which clips anything
            // past its right edge, including half of this button and its
            // icon. Sitting fully inside that boundary instead so the
            // whole circle — and the chevron in it — actually renders.
            right: "4px",
            top: isCollapsed ? "9px" : "55px",
            zIndex: 9999,
          }}
        >
          {!isCollapsed ? (
            <ChevronLeft
              size={18}
              strokeWidth={3}
              color="var(--vi-accent-on)"
            />
          ) : (
            <ChevronRight
              size={18}
              strokeWidth={3}
              color="var(--vi-accent-on)"
            />
          )}
        </button>
      </div>

      {/* Scrollable nav list — flex-1 + min-h-0 is what lets this region
          (and only this region) scroll while the logo above and the
          bottom section below stay fixed. */}
      <div
        className={`flex flex-col !w-full flex-1 min-h-0 overflow-y-auto !overflow-x-hidden scrollbar-hide mt-2 ${
          isCollapsed ? "items-center" : "items-start"
        }`}
      >
        <div
          className={`h-full transition-all  duration-300 ${
            isCollapsed ? "w-20" : "w-full max-w-[210px]"
          }`}
        >
          {items.map((item: any, key: number) => {
            const ItemIcon = SIDEBAR_ICON_MAP[item.icon] || FileText;
            const isActive = selected === item.label;
            const rowClasses = `vi-sidebar-nav-item group flex items-center gap-x-3 !font-inter pl-[10px] pr-2 mb-[10px] rounded-md h-10 cursor-pointer border-l-2 transition-colors duration-200 ${
              isActive ? "vi-sidebar-nav-item-active" : ""
            }`;
            const iconClasses = `shrink-0 transition-colors duration-200 ${
              isActive
                ? "text-[color:var(--vi-accent)]"
                : "text-[color:var(--vi-text-tertiary)] group-hover:text-[color:var(--vi-accent)]"
            }`;

            return (
              <Entitlement
                allowedKeys={
                  isAdminUserOne && item.allowAdminWithoutEntitlement
                    ? undefined
                    : item.entitlements
                }
                key={key}
              >
                {!hasSidebarAccess(item) ? null : (
                  <Link
                    id={item.label}
                    to={item.path}
                    key={key}
                    onClick={handleMenuSelection}
                  >
                    {isCollapsed ? (
                      <Tooltip
                        popupVisible={isCollapsed}
                        placement="top"
                        title={item.label}
                        overlayClassName="custom-tooltip"
                        color="white"
                      >
                        <div className={`${rowClasses} justify-center`}>
                          <ItemIcon
                            size={18}
                            strokeWidth={1.75}
                            className={iconClasses}
                          />
                        </div>
                      </Tooltip>
                    ) : (
                      <div className={rowClasses}>
                        <ItemIcon
                          size={18}
                          strokeWidth={1.75}
                          className={iconClasses}
                        />
                        <Caption
                          id={item.label}
                          onClick={handleMenuSelection}
                          classes="inline !text-[14px] !font-normal !font-inter"
                          color={
                            isActive
                              ? "text-[color:var(--vi-accent)]"
                              : "text-[color:var(--vi-text)]"
                          }
                        >
                          {item.label}
                        </Caption>
                      </div>
                    )}
                  </Link>
                )}
              </Entitlement>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col gap-y-4 shrink-0 pb-4">
        {/* Login Card - Show only when not logged in and not collapsed */}
        {!username && !isCollapsed && (
          <div className="mx-2">
            {/* Redesigned from a flat surface + plain-blue button into a
                small glass "upsell" card: an accent-tinted gradient wash
                with a soft glow blob in the corner, an icon badge next
                to the heading, and the CTA button carrying the same
                --vi-accent-grad every other primary CTA in the app uses
                instead of a flat --vi-accent fill. */}
            <div
              className="relative overflow-hidden rounded-lg border p-3 pt-4 flex flex-col gap-3 transition-all duration-300"
              style={{
                background:
                  "linear-gradient(160deg, color-mix(in srgb, var(--vi-accent) 16%, var(--vi-surface)) 0%, var(--vi-surface) 75%)",
                borderColor:
                  "color-mix(in srgb, var(--vi-accent) 30%, var(--vi-card-border))",
                boxShadow: "var(--vi-shadow-card)",
              }}
            >
              <div
                className="pointer-events-none absolute -top-6 -right-6 w-20 h-20 rounded-full opacity-30 blur-2xl"
                style={{ background: "var(--vi-accent)" }}
              />

              <div className="relative flex items-center gap-2">
                <span
                  className="flex items-center justify-center w-7 h-7 rounded-md shrink-0"
                  style={{ background: "var(--vi-accent-grad)" }}
                >
                  <Sparkles
                    size={14}
                    style={{ color: "var(--vi-accent-on)" }}
                  />
                </span>
                <h3 className="text-sm font-semibold text-[color:var(--vi-text)] leading-tight">
                  Unlock all features
                </h3>
              </div>

              <p className="relative text-xs text-[color:var(--vi-text-secondary)] leading-relaxed">
                Login to access advanced analytics and exclusive insights
              </p>

              <Button
                type="primary"
                size="small"
                onClick={handleLogIn}
                icon={<LogIn size={14} />}
                className="relative !border-none !w-full !h-8 !flex !items-center !justify-center !gap-1.5 !font-semibold hover:!opacity-90"
                style={{
                  background: "var(--vi-accent-grad)",
                  color: "var(--vi-accent-on)",
                }}
              >
                Login
              </Button>
            </div>
          </div>
        )}

        <div
          className={`${
            // The currency Select is `!w-full` up to its own !max-w-[140px]
            // cap — with no horizontal padding here it stretched flush to
            // this now-narrower (84px, collapsed) column's edges. px-2 only
            // in the collapsed case so the expanded row (already fine)
            // isn't affected.
            isCollapsed ? " flex flex-col px-2" : ""
          } flex gap-2 items-center justify-center mt-2 `}
        >
          <Select
            onChange={handleChange}
            options={currencyOptions}
            defaultValue={defaultCurrency}
            value={defaultCurrency}
            placeholder="Currency"
            // The visible box is actually antd's inner `.ant-select-
            // selector` div, not the root <span> this className prop
            // attaches to — a Tailwind class here can size/position the
            // root span (width, height, radius via clipping) but can't
            // set that inner div's own background/border/text color,
            // which is why they stayed at antd's default white/gray
            // despite the `!border-[...]` class below. Real color
            // overrides for both that inner div and the dropdown list
            // (which portals to <body>, reachable only via
            // popupClassName) live in sidebar.theme.css instead, keyed
            // off this "vi-sidebar-currency-select" class.
            className="vi-sidebar-currency-select !w-full !font-inter !max-w-[140px] !outline-none focus:!outline-none !border-[color:var(--vi-card-border)] !h-[30px] !rounded-[8px] !border focus:!border-none placeholder:!text-[color:var(--vi-text-tertiary)]"
            popupClassName="vi-sidebar-currency-popup"
          />
          <DarkModeSwitcher
            darkMode={darkMode}
            toggleDarkMode={toggleDarkMode}
          />
          <PaletteSwitcher palette={palette} togglePalette={togglePalette} />
        </div>

        <div
          className={`w-full !font-inter flex items-center justify-between !gap-x-2 ${
            isCollapsed ? "w-20" : "max-w-[210px]"
          } border-t border-[color:var(--vi-card-border)]`}
        >
          {bottomItems.map((item: any, key: number) => {
            const ItemIcon = SIDEBAR_ICON_MAP[item.icon] || FileText;
            const isActive = selected === item.label;

            return (
              <Entitlement allowedKeys={item.entitlements} key={key}>
                <Link
                  id={item.label}
                  to={item.path}
                  key={key}
                  onClick={handleMenuSelection}
                  className="w-full"
                >
                  <div
                    className={`vi-sidebar-nav-item group flex items-center gap-x-3 h-10 !w-full cursor-pointer rounded-md mt-2 pl-[10px] pr-2 border-l-2 transition-colors duration-200 ${
                      isActive ? "vi-sidebar-nav-item-active" : ""
                    } ${isCollapsed ? "justify-center" : ""}`}
                  >
                    <ItemIcon
                      size={18}
                      strokeWidth={1.75}
                      className={`shrink-0 transition-colors duration-200 ${
                        isActive
                          ? "text-[color:var(--vi-accent)]"
                          : "text-[color:var(--vi-text-tertiary)] group-hover:text-[color:var(--vi-accent)]"
                      }`}
                    />
                    {!isCollapsed && (
                      <Caption
                        id={item.label}
                        onClick={handleMenuSelection}
                        classes="inline !text-[15px] !font-inter !font-[500]"
                        color={
                          isActive
                            ? "text-[color:var(--vi-accent)]"
                            : "text-[color:var(--vi-text)]"
                        }
                      >
                        {item.label}
                      </Caption>
                    )}
                  </div>
                </Link>
              </Entitlement>
            );
          })}

          {username && (
            <LogOut
              onClick={handleLogout}
              size={20}
              className="text-[color:var(--vi-text-tertiary)] w-10 mt-2 cursor-pointer transition-colors duration-200 hover:text-[color:var(--vi-accent)]"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
